import { axios } from '/plugins/axios.client';
import { errorFromResponse } from '/src/common/general-utils';
import { i18n } from '/plugins/i18n.client';
import { widgetForState } from '/src/features/widgets/edit/widget-helper';

const errorHandler = errorFromResponse(i18n.global.t.bind(i18n));
import { toast } from '/plugins/toaster.client.js';

/**
 * Fetch details of Object Template by UUID
 * @param {String} templateUUID UUID of the Object Template whose details are to be fetched
 */
export const getObjectTemplate = async (templateUUID) => {
  try {
    const objectTemplate = await axios.$get(`/api/objectUI/${templateUUID}`);
    const actionsMap = (await axios.$get(`/api/triggers/Object Template/${templateUUID}?withActions=true`)).reduce(
      (actionMap, currentTrigger) => ({
        ...actionMap,
        ...currentTrigger.Actions.reduce(
          (curActionList, curAction) => ({
            ...curActionList,
            [curAction.UUID]: curAction
          }),
          {}
        )
      }),
      {}
    );

    objectTemplate.conditions = objectTemplate.conditions || '[]';
    objectTemplate.dashboardOptions = objectTemplate.dashboardOptions || '{}';
    objectTemplate.formbuilderJSON = objectTemplate.formbuilderJSON || '[]';
    objectTemplate.formSettingJSON = objectTemplate.formSettingJSON || '{}';
    objectTemplate.dataLoaderSettings = objectTemplate.dataLoaderSettings || '{ "dataLoaderSettings": [] }';
    const conditions = _setConditions(objectTemplate.conditions.sParseJSON([]), actionsMap);

    return {
      ...objectTemplate,
      conditions,
      dashboardOptions: objectTemplate.dashboardOptions.sParseJSON({}),
      formbuilderJSON: objectTemplate.formbuilderJSON.sParseJSON([]),
      formSettingJSON: objectTemplate.formSettingJSON.sParseJSON({}),
      dataLoaderSettings: objectTemplate.dataLoaderSettings.sParseJSON({ dataLoaderSettings: [] })
    };
  } catch (error) {
    toast.error(errorHandler(i18n.global.t('common.object_templates.fetch_object_template'), error));
    return null;
  }
};

const _setConditions = (conditions, actionsMap) =>
  conditions.map((cluster) => {
    if (cluster.triggerUUID) {
      cluster.actions = cluster.actions
        .map((action) => {
          if (action.type === 'TRIGGER') {
            action.config = actionsMap[action.config.UUID];
          }
          // removing trigger that does not have any action configured
          if (action.type === 'TRIGGER' && action.config === undefined) return undefined;
          return action;
        })
        .filter((action) => action !== undefined);
    }
    return cluster;
  });

/**
 * Fetch widgets of Object Template
 * @param {String} templateUUID UUID of the Object Template whose details are to be fetched
 */
export const getWidgets = async (templateUUID, restrictTypes = null, fromTemplate = true) => {
  try {
    let widgets = await axios.$get('/api/widgets/loadWidgets', {
      params: { pageUUID: templateUUID, restrictTypes: restrictTypes },
      headers: { 'from-template': fromTemplate }
    });
    return widgets.map((widget) => widgetForState(widget));
  } catch (error) {
    toast.error(errorHandler(i18n.global.t('common.object_templates.fetch_widgets_error'), error));
    return [];
  }
};

/**
 * Fetch All State Transitions
 */
export const getAllTransitions = async () => {
  try {
    return await axios.$get('/api/workflow/transitions/list');
  } catch (error) {
    toast.error(
      errorHandler(
        i18n.global.t('admin.dynamic_labels.fetch_action', {
          feature: i18n.global.t('admin.objects.templates.tab_conditions.complete_task.label')
        }),
        error
      )
    );
    return [];
  }
};

export const getFieldsFromBusinessruleUUID = async (uuid) => await axios.$get('/api/profiler-businessrule/' + uuid);

/**
 * Fetch widget details by widget uuid
 */
export const getWidget = async (widgetUUID, templateUUID) => {
  try {
    const widget = await axios.$get(`/api/v2/widgets/${widgetUUID}/byRef`, {
      headers: { 'from-template': true, 'template-uuid': templateUUID }
    });
    return widgetForState(widget);
  } catch (error) {
    toast.error(
      errorHandler(
        i18n.global.t('admin.dynamic_labels.fetch_action', {
          feature: i18n.global.t('admin.feature.widgets')
        }),
        error
      )
    );
    return {};
  }
};

export const getTemplateList = async (params) => {
  try {
    return await axios.$get('/api/objectUI/select-values', { params });
  } catch (error) {
    return [];
  }
};
